import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import clone from 'ramda/src/clone';
import concat from 'ramda/src/concat';
import equals from 'ramda/src/equals';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import keys from 'ramda/src/keys';
import move from 'ramda/src/move';
import propEq from 'ramda/src/propEq';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Toggle from '@components/@core/Toggle';
import CompanyActions from '~/actions/company';

const Edit = ({ data, onDiscard, businessId }) => {
    const dispatch = useDispatch();
    const refDeps = useRef();

    const [departments, setDepartments] = useState(data?.departments);
    const [departmentDeletedIds, setDepartmentDeletedIds] = useState([]);
    const [checked, setChecked] = useState(true);
    const [teams, setTeams] = useState({});
    const [roles, setRoles] = useState({});
    const [initData, setInitData] = useState({});

    const handleAddDepartment = () => {
        const temp = clone(departments);
        temp.push({
            _id: departments.length,
            name: '',
            company_id: data.id,
            data: { exit_id: 0, placement_id: 0, satisfaction_id: 0 },
        });
        setDepartments(temp);
    };

    const handleDepartment = (e, index) => {
        const temp = clone(departments);
        temp[index].name = e;
        setDepartments(temp);
    };

    const handleSelectMenu = (item) => (e) => {
        if (e === 'delete') {
            handleDeleteDepartment(item.id);
        } else {
            const index = findIndex(propEq('id', item.id), departments);
            let temp = clone(departments);
            temp = move(index, e === 'move up' ? index - 1 : index + 1, temp);
            setDepartments(temp);
        }
    };

    const handleDeleteDepartment = (id) => {
        const department = find(propEq('id', id), departments);
        const temp = filter((item) => item.id !== id, departments);
        setDepartments(temp);
        setDepartmentDeletedIds([...departmentDeletedIds, department.id]);
        toast.error(`This will delete this ${department.name}, its contents will become unassigned.`);
    };

    const handleAddTeam = (id) => {
        const temp = clone(teams);
        if (temp[id])
            temp[id].push({
                company_id: data.id,
                department_id: departments[id]?.id,
                _department_id: departments[id]?._id,
                name: '',
                data: { users: [], managers: [] },
            });
        else
            temp[id] = [
                {
                    company_id: data.id,
                    department_id: departments[id]?.id,
                    _department_id: departments[id]?._id,
                    name: '',
                    data: { users: [], managers: [] },
                },
            ];
        setTeams(temp);
    };

    const handleTeam = (id, index, e) => {
        if (!isEmpty(e)) {
            const temp = clone(teams);
            temp[id][index].name = e;
            setTeams(temp);
        }
    };

    const handleAddRole = (id) => {
        const temp = clone(roles);

        if (temp[id])
            temp[id].push({
                name: '',
                department_id: departments[id]?.id,
                _department_id: departments[id]?._id,
                titles: [],
                data: {},
            });
        else temp[id] = [{ name: '', department_id: departments[id]?.id, _department_id: departments[id]?._id, titles: [], data: {} }];
        setRoles(temp);
    };

    const handleClear = (item, id) => {
        dispatch(CompanyActions.postdeleteteamRequest(item.company_id, item.id));
        const _teams = clone(teams);
        _teams[id] = filter((e) => e.id !== item.id, teams[id]);
        setTeams(_teams);
    };

    const handleRole = (id, index, e) => {
        const temp = clone(roles);
        temp[id][index].name = e;
        setRoles(temp);
    };

    const handleTeamRoleSave = (e) => {
        let job_roles = [];

        keys(roles).forEach((key) => {
            for (const tempR of roles[key]) {
                if (isNil(tempR.department_id)) {
                    let dep_name = find(propEq('_id', tempR._department_id), refDeps.current).name;
                    let dep_id = find(propEq('name', dep_name), e).id;
                    tempR.department_id = dep_id;
                }
                job_roles = concat(job_roles, [tempR]);
            }
        });

        const payloadJ = { job_roles };
        dispatch(CompanyActions.postsavejobrolesRequest(data.id, payloadJ));

        let temp = [];
        keys(teams).forEach((key) => {
            for (const tempT of teams[key]) {
                if (isNil(tempT.department_id)) {
                    let dep_name = find(propEq('_id', tempT._department_id), refDeps.current).name;
                    let dep_id = find(propEq('name', dep_name), e).id;
                    tempT.department_id = dep_id;
                }
                temp = concat(temp, [tempT]);
            }
        });
        const _teams = filter((e) => e.name !== '', temp);
        const payloadT = { teams: _teams };
        dispatch(CompanyActions.postaddeditteamsRequest(data.id, payloadT));
        onDiscard();
    };

    const handleSave = () => {
        const payload = { departments };
        refDeps.current = departments;
        departmentDeletedIds.forEach((departmentId) => {
            dispatch(CompanyActions.postdeletedepartmentRequest(businessId, data.id, departmentId));
        });
        setDepartmentDeletedIds([]);
        dispatch(CompanyActions.postaddeditdepartmentsRequest(data.id, payload, handleTeamRoleSave));
    };

    const handleDiscard = () => {
        setDepartmentDeletedIds([]);
        onDiscard();
    };

    const isNoChange = () => {
        return equals(data.departments, departments) && equals(teams, initData.teams) && equals(roles, initData.roles);
    };

    useEffect(() => {
        const tempT = {};
        const tempR = {};
        departments.forEach((dep, index) => {
            const _teams = data.teams.filter((e) => e.department_id === dep.id);
            tempT[index] = _teams;
            const _roles = data.job_roles.filter((e) => e.department_id === dep.id);
            tempR[index] = _roles;
        });
        setTeams(tempT);
        setRoles(tempR);
        setInitData({ teams: tempT, roles: tempR });
    }, []);

    useEffect(() => {
        setDepartments(data?.departments);
    }, [JSON.stringify(data?.departments)]);

    return (
        <div className="human-capital">
            <div className="d-flex align-items-center mt-4 mb-4">
                <span className="dsl-b17 text-400">Departments</span>
                <Icon name="fas fa-circle-plus cursor-pointer ms-3" color="#969faa" size={16} onClick={handleAddDepartment} />
            </div>
            {departments.map((item, index) => {
                let dotsMenu = ['Move Down', 'Move Up', 'Delete'];
                if (index === 0) dotsMenu = filter((x) => x !== 'Move Up', dotsMenu);
                if (index + 1 === departments.length) dotsMenu = filter((x) => x !== 'Move Down', dotsMenu);
                return (
                    <div className="d-flex ai-center mb-3" key={index}>
                        <Input
                            className="edit-row me-3"
                            key={`c${index}`}
                            title={`Department ${index + 1}`}
                            placeholder="Type here..."
                            value={departments[index]?.name || ''}
                            onChange={(e) => handleDepartment(e, index)}
                        />
                        <EditDropdown options={dotsMenu} onChange={handleSelectMenu(item)} />
                    </div>
                );
            })}
            <p className="dsl-b20 bold mt-5 mb-5">Teams</p>
            <span className="dsl-b12 mb20">Team within departments?</span>
            <Toggle checked={checked} leftLabel="Yes" rightLabel="No" onChange={(e) => setChecked(e)} />
            {departments.map((dep, id) => (
                <div key={`dt${id}`}>
                    <div className="d-flex align-items-center mt-5 mb-4">
                        <span className="dsl-b17 text-400">{dep.name}</span>
                        <Icon name="fas fa-circle-plus cursor-pointer ms-3" color="#969faa" size={16} onClick={() => handleAddTeam(id)} />
                    </div>
                    {teams[id]?.map((item, index) => (
                        <Input
                            className="edit-row mb-3 me-5 ps-3"
                            key={`t${id}${index}`}
                            title={`Team ${index + 1}`}
                            placeholder="Type here..."
                            value={item.name}
                            onClear={() => handleClear(item, id)}
                            onChange={(e) => handleTeam(id, index, e)}
                        />
                    ))}
                </div>
            ))}
            <p className="dsl-b20 bold">Roles in Departments</p>
            {departments.map((dep, id) => (
                <div key={`dr${id}`}>
                    <div className="d-flex align-items-center mt-5 mb-4">
                        <span className="dsl-b17 text-400">{dep.name}</span>
                        <Icon name="fas fa-circle-plus cursor-pointer ms-3" color="#969faa" size={16} onClick={() => handleAddRole(id)} />
                    </div>
                    {roles[id]?.map((item, index) => (
                        <Input
                            className="edit-row mb-3 me-5 ps-3"
                            key={`r${id}${index}`}
                            title={`Role ${index + 1}`}
                            placeholder="Type here..."
                            value={item.name}
                            onChange={(e) => handleRole(id, index, e)}
                        />
                    ))}
                </div>
            ))}
            <div className="d-flex justify-content-end pe-3">
                <Button className="mr-3" name="DISCARD" type="low" onClick={handleDiscard} />
                <Button className="pl-3 pe-3" name="SAVE" disabled={isNoChange()} onClick={handleSave} />
            </div>
        </div>
    );
};

Edit.propTypes = {
    data: PropTypes.array,
    checked: PropTypes.bool,
};

Edit.defaultProps = {
    data: [{ name: 'BDC' }, { name: 'Finance' }],
    checked: true,
};

export default Edit;
